import { AdvancePermission, AdvancePermissionWrapper } from '@/models/AdvancePermission';
import { Deserializer } from 'jsonapi-serializer';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { APIConnector } from './api/APIConnector';
import { getConstraintViolation, processServerError } from './utilities/ServiceError';
import { createPagination } from './utilities/paginationBuilder';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { deleteUserPreference } from './userPreferenceService';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from '@/constants/response-status-codes';

const apiConnector = new APIConnector();

export const getUserAdvancePermissions = async (userLoginId: string, listParameters?: ListQuery): Promise<AdvancePermissionWrapper> => {
    try {
        const defaultFilter = `userPreferenceType.id==8;userLogin.id==${userLoginId}`;
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = filter.length > 0 ? `${filter};${defaultFilter}` : `&filter=${defaultFilter}`;
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const settingFields = 'fields[settingType]=trnName';
        const userPreferenceFields = 'fields[userPreference]=settingType';
        const url = `userPreference?include=settingType&${settingFields}&${userPreferenceFields}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const advancePermissions: AdvancePermission[] = data.map((permission) => {
                    return {
                        id: permission['id'],
                        name: permission['setting-type']['trn-name'],
                        settingTypeId: Number(permission['setting-type']['id']),
                    }
                });

                return resolve({ rows: advancePermissions, pagination })
            });
        });

    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getAdvancePermissions = async (listParameters?: ListQuery): Promise<AdvancePermissionWrapper> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = filter.length > 0 ? filter + ';stSysname==USER_CAPABILITIES_*' : '&filter=stSysname==USER_CAPABILITIES_*';
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `settingType?fields[settingType]=trnName` + partialURL;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const advancePermissions: AdvancePermission[] = data.map(permission => {
                    return {
                        id: permission['id'],
                        name: permission['trn-name'],
                        settingTypeId: Number(permission['id'])
                    }
                });

                return resolve({ rows: advancePermissions, pagination })
            });
        });

    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

const storeAdvancePermission = async (userLoginId: string, settingTypeId: string): Promise<any> => {
    try {
        const payload = {
            data: {
                type: "userPreference",
                attributes: {},
                relationships: {
                    settingType: {
                        data: {
                            type: "settingType",
                            id: settingTypeId
                        }
                    },
                    userPreferenceType: {
                        data: {
                            type: "userPreferenceType",
                            id: "8" // 8 is the id for Advance Permission user preference type
                        }
                    },
                    userLogin: {
                        data: {
                            type: "userLogin",
                            id: userLoginId
                        }
                    }
                }
            }
        }

        const url = `userPreference`;
        const response: Response = await apiConnector.sendData(url, true, payload);

        return new Promise(async (resolve, reject) => {
            if (response) {
                const content = response.status != NO_CONTENT ? await response.json() : null;

                if (content.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                }
                new Deserializer().deserialize(content, (error, newUserPreference) => {
                    if (error) {

                        return reject({ status: response.status, id: newUserPreference.id, success: false, message: genericError });
                    }

                    return resolve({ id: newUserPreference.id, success: true });
                })
                return;
            }
            return reject({ status: INTERNAL_SERVER_ERROR, success: false, message: genericError });

        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const assignUnassignAdvancePermission = async (toBeUnassigned: string[], toBeAssigned: string[], userLoginId: string): Promise<any> => {
    try {
        const unassignedPromises = toBeUnassigned.map(async (userPreferenceId) => {
            return await deleteUserPreference(userPreferenceId);
        });

        const assignedPromises = toBeAssigned.map(async (settingTypeId) => {
            return await storeAdvancePermission(userLoginId, settingTypeId);
        });

        await Promise.all([...unassignedPromises, ...assignedPromises]);
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}
